import _ from 'lodash';
import { format, isToday, isValid, parse } from 'date-fns';

export function toInteger(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
}

export function normalizeId(value) {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    return String(value);
}

export function normalizeApiDate(value) {
    if (!value) {
        return '';
    }

    if (value instanceof Date && isValid(value)) {
        return format(value, 'yyyy-MM-dd');
    }

    const stringValue = String(value);
    const parsed = parse(stringValue, 'yyyy-MM-dd', new Date());

    return isValid(parsed) && format(parsed, 'yyyy-MM-dd') === stringValue
        ? stringValue
        : '';
}

export function normalizeApiTime(value) {
    if (!value) {
        return '';
    }

    if (value instanceof Date && isValid(value)) {
        return format(value, 'HH:mm');
    }

    const match = String(value).match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!match) {
        return '';
    }

    const hours = toInteger(match[1], -1);
    const minutes = toInteger(match[2], -1);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return '';
    }

    return (hours < 10 ? '0' : '') + hours + ':' + (minutes < 10 ? '0' : '') + minutes;
}

export function getDefaultSeatSides(shape, seats) {
    if (shape !== 'square') {
        return null;
    }

    const totalSeats = toInteger(seats, 4);
    const top = Math.ceil(totalSeats / 2);

    return {
        top: top,
        bottom: Math.max(0, totalSeats - top),
        left: 0,
        right: 0
    };
}

export function normalizeSeatSides(seatSides, shape, seats) {
    if (shape !== 'square') {
        return null;
    }

    return _.assign(
        {},
        getDefaultSeatSides('square', seats),
        seatSides || {}
    );
}

export function normalizeRoom(room) {
    return {
        id: normalizeId(_.get(room, 'id')),
        name: _.get(room, 'name', ''),
        width: toInteger(_.get(room, 'width'), 600),
        height: toInteger(_.get(room, 'height'), 400),
        position: toInteger(_.get(room, 'position'), 0)
    };
}

export function normalizeTable(table) {
    const shape = _.get(table, 'shape', 'round') || 'round';
    const seats = toInteger(_.get(table, 'seats'), shape === 'square' ? 4 : 2);

    return {
        id: normalizeId(_.get(table, 'id')),
        roomId: normalizeId(_.get(table, 'roomId') || _.get(table, 'room.id')),
        number: _.get(table, 'number') != null ? String(_.get(table, 'number')) : '',
        name: _.get(table, 'name', ''),
        shape: shape,
        x: toInteger(_.get(table, 'x'), 100),
        y: toInteger(_.get(table, 'y'), 100),
        width: toInteger(_.get(table, 'width'), shape === 'square' ? 110 : 80),
        height: toInteger(_.get(table, 'height'), shape === 'square' ? 90 : 80),
        rotation: toInteger(_.get(table, 'rotation'), 0),
        seats: seats,
        seatSides: normalizeSeatSides(_.get(table, 'seatSides'), shape, seats),
        blocked: !!_.get(table, 'blocked', false)
    };
}

const FIXTURE_DEFAULT_SIZES = {
    rectangle: { width: 160, height: 60 },
    circle: { width: 120, height: 120 },
    triangle: { width: 140, height: 120 },
    line: { width: 240, height: 4 },
    stairs: { width: 90, height: 140 },
    'spiral-stairs': { width: 120, height: 120 },
    'area-link': { width: 160, height: 220 }
};

export function normalizeFixtureDirection(direction) {
    return direction === 'down' || direction === 'both' ? direction : 'up';
}

export function getFixtureDefaultSize(type) {
    return FIXTURE_DEFAULT_SIZES[type] || FIXTURE_DEFAULT_SIZES.rectangle;
}

export function normalizeFixture(fixture) {
    const type = _.get(fixture, 'type', 'rectangle') || 'rectangle';
    const defaultSize = getFixtureDefaultSize(type);

    return {
        id: normalizeId(_.get(fixture, 'id')),
        roomId: normalizeId(_.get(fixture, 'roomId') || _.get(fixture, 'room.id')),
        targetRoomId: normalizeId(_.get(fixture, 'targetRoomId')),
        targetRoomDownId: normalizeId(_.get(fixture, 'targetRoomDownId')),
        type: type,
        label: _.get(fixture, 'label', '') || '',
        direction: normalizeFixtureDirection(_.get(fixture, 'direction')),
        x: toInteger(_.get(fixture, 'x'), 100),
        y: toInteger(_.get(fixture, 'y'), 100),
        width: toInteger(_.get(fixture, 'width'), defaultSize.width),
        height: toInteger(_.get(fixture, 'height'), defaultSize.height),
        rotation: toInteger(_.get(fixture, 'rotation'), 0),
        position: toInteger(_.get(fixture, 'position'), 0)
    };
}

export function buildFixtureInput(fixture) {
    const normalizedFixture = normalizeFixture(fixture);

    return {
        id: normalizedFixture.id ? toInteger(normalizedFixture.id, null) : undefined,
        room: {
            id: toInteger(normalizedFixture.roomId, null)
        },
        targetRoomId: normalizedFixture.targetRoomId
            ? toInteger(normalizedFixture.targetRoomId, null)
            : null,
        targetRoomDownId: normalizedFixture.targetRoomDownId
            ? toInteger(normalizedFixture.targetRoomDownId, null)
            : null,
        type: normalizedFixture.type,
        label: normalizedFixture.label,
        direction: normalizedFixture.direction,
        x: normalizedFixture.x,
        y: normalizedFixture.y,
        width: normalizedFixture.width,
        height: normalizedFixture.height,
        rotation: normalizedFixture.rotation,
        position: normalizedFixture.position
    };
}

export function normalizeReservation(reservation) {
    return {
        id: normalizeId(_.get(reservation, 'id')),
        guestName: _.get(reservation, 'guestName', ''),
        guestPhone: _.get(reservation, 'guestPhone', ''),
        guestEmail: _.get(reservation, 'guestEmail', ''),
        partySize: toInteger(_.get(reservation, 'partySize'), 2),
        date: _.get(reservation, 'date', ''),
        time: _.get(reservation, 'time', ''),
        duration: toInteger(_.get(reservation, 'duration'), 90),
        tableIds: (_.get(reservation, 'tableIds') || []).map(normalizeId).filter(Boolean),
        status: _.get(reservation, 'status', 'pending') || 'pending',
        notes: _.get(reservation, 'notes', '')
    };
}

export function normalizePresetButtonSize(size) {
    return size === 'large' || size === 'xlarge' ? size : 'default';
}

export function normalizeNoteTags(tags) {
    return _.uniq(
        (tags || [])
            .map((tag) => { return String(tag || '').trim(); })
            .filter(Boolean)
    );
}

export function normalizeServiceTimes(serviceTimes) {
    return (serviceTimes || [])
        .map((window) => {
            const weekday = toInteger(_.get(window, 'weekday'), 0);
            const from = normalizeApiTime(_.get(window, 'from'));
            const to = normalizeApiTime(_.get(window, 'to'));

            if (weekday < 1 || weekday > 7 || !from || !to || from >= to) {
                return null;
            }

            return { weekday: weekday, from: from, to: to };
        })
        .filter(Boolean)
        .sort((a, b) => {
            return a.weekday - b.weekday || a.from.localeCompare(b.from);
        });
}

export function normalizeClosedDates(closedDates) {
    return _.uniq(
        (closedDates || [])
            .map((date) => { return normalizeApiDate(date); })
            .filter(Boolean)
    ).sort();
}

export function normalizeNotificationRecipients(recipients) {
    return _.uniq(
        (recipients || [])
            .map((email) => { return String(email || '').trim(); })
            .filter(Boolean)
    );
}

export function normalizeSettings(settings) {
    const mode = _.get(settings, 'tablePlacementMode');
    return {
        id: normalizeId(_.get(settings, 'id')),
        defaultDuration: toInteger(_.get(settings, 'defaultDuration'), 90),
        tablePlacementMode: mode === 'automatic' ? 'automatic' : 'manual',
        touchKeyboardAutoOpen: !!_.get(settings, 'touchKeyboardAutoOpen', false),
        presetButtonSize: normalizePresetButtonSize(_.get(settings, 'presetButtonSize')),
        noteTags: normalizeNoteTags(_.get(settings, 'noteTags')),
        onlineEnabled: !!_.get(settings, 'onlineEnabled', false),
        serviceTimes: normalizeServiceTimes(_.get(settings, 'serviceTimes')),
        closedDates: normalizeClosedDates(_.get(settings, 'closedDates')),
        slotInterval: toInteger(_.get(settings, 'slotInterval'), 30),
        leadTime: toInteger(_.get(settings, 'leadTime'), 120),
        maxAdvanceDays: toInteger(_.get(settings, 'maxAdvanceDays'), 90),
        minPartySize: toInteger(_.get(settings, 'minPartySize'), 1),
        maxPartySize: toInteger(_.get(settings, 'maxPartySize'), 10),
        autoConfirm: !!_.get(settings, 'autoConfirm', true),
        guestConfirmationEnabled: !!_.get(settings, 'guestConfirmationEnabled', true),
        notificationRecipients: normalizeNotificationRecipients(_.get(settings, 'notificationRecipients')),
        onlineHint: _.get(settings, 'onlineHint', '') || ''
    };
}

export function normalizeCombination(combination) {
    return {
        id: normalizeId(_.get(combination, 'id')),
        name: _.get(combination, 'name', '') || '',
        position: toInteger(_.get(combination, 'position'), 0),
        tableIds: (_.get(combination, 'tableIds') || []).map(normalizeId).filter(Boolean),
        totalSeats: toInteger(_.get(combination, 'totalSeats'), 0)
    };
}

export function buildCombinationInput(combination, cmsId) {
    const payload = {
        name: _.get(combination, 'name', '') || '',
        position: toInteger(_.get(combination, 'position'), 0),
        tableIds: (_.get(combination, 'tableIds') || []).map((tableId) => {
            return toInteger(tableId, null);
        }).filter(Boolean)
    };

    if (_.get(combination, 'id')) {
        payload.id = toInteger(_.get(combination, 'id'), null);
    }

    if (cmsId !== null && cmsId !== undefined) {
        payload.cms = {
            id: toInteger(cmsId, null)
        };
    }

    return payload;
}

export function buildReservationInput(reservation, cmsId = null) {
    const payload = {
        guestName: _.get(reservation, 'guestName', ''),
        guestPhone: _.get(reservation, 'guestPhone', '') || null,
        guestEmail: _.get(reservation, 'guestEmail', '') || null,
        partySize: toInteger(_.get(reservation, 'partySize'), 1),
        date: normalizeApiDate(_.get(reservation, 'date', '')),
        time: normalizeApiTime(_.get(reservation, 'time', '')),
        duration: toInteger(_.get(reservation, 'duration'), 90),
        tableIds: (_.get(reservation, 'tableIds') || []).map((tableId) => {
            return toInteger(tableId, null);
        }).filter(Boolean),
        status: _.get(reservation, 'status', 'pending'),
        notes: _.get(reservation, 'notes', '') || null
    };

    if (_.get(reservation, 'id')) {
        payload.id = toInteger(_.get(reservation, 'id'), null);
    }

    if (cmsId !== null && cmsId !== undefined) {
        payload.cms = {
            id: toInteger(cmsId, null)
        };
    }

    return payload;
}

export function buildRoomInput(room, cmsId = null) {
    const payload = {
        name: _.get(room, 'name', ''),
        width: toInteger(_.get(room, 'width'), 600),
        height: toInteger(_.get(room, 'height'), 400),
        position: toInteger(_.get(room, 'position'), 0)
    };

    if (_.get(room, 'id')) {
        payload.id = toInteger(_.get(room, 'id'), null);
    }

    if (cmsId !== null && cmsId !== undefined) {
        payload.cms = {
            id: toInteger(cmsId, null)
        };
    }

    return payload;
}

export function buildTableInput(table) {
    const normalizedTable = normalizeTable(table);

    return {
        id: normalizedTable.id ? toInteger(normalizedTable.id, null) : undefined,
        room: {
            id: toInteger(normalizedTable.roomId, null)
        },
        number: normalizedTable.number || null,
        name: normalizedTable.name,
        shape: normalizedTable.shape,
        x: normalizedTable.x,
        y: normalizedTable.y,
        width: normalizedTable.width,
        height: normalizedTable.height,
        rotation: normalizedTable.rotation,
        seats: normalizedTable.seats,
        seatSides: normalizedTable.seatSides,
        blocked: normalizedTable.blocked
    };
}

export function buildSettingsInput(settings, cmsId) {
    const mode = _.get(settings, 'tablePlacementMode');
    const payload = {
        cms: {
            id: toInteger(cmsId, null)
        },
        defaultDuration: toInteger(_.get(settings, 'defaultDuration'), 90),
        tablePlacementMode: mode === 'automatic' ? 'automatic' : 'manual',
        touchKeyboardAutoOpen: !!_.get(settings, 'touchKeyboardAutoOpen', false),
        presetButtonSize: normalizePresetButtonSize(_.get(settings, 'presetButtonSize')),
        noteTags: normalizeNoteTags(_.get(settings, 'noteTags')),
        onlineEnabled: !!_.get(settings, 'onlineEnabled', false),
        serviceTimes: normalizeServiceTimes(_.get(settings, 'serviceTimes')),
        closedDates: normalizeClosedDates(_.get(settings, 'closedDates')),
        slotInterval: toInteger(_.get(settings, 'slotInterval'), 30),
        leadTime: toInteger(_.get(settings, 'leadTime'), 120),
        maxAdvanceDays: toInteger(_.get(settings, 'maxAdvanceDays'), 90),
        minPartySize: toInteger(_.get(settings, 'minPartySize'), 1),
        maxPartySize: toInteger(_.get(settings, 'maxPartySize'), 10),
        autoConfirm: !!_.get(settings, 'autoConfirm', true),
        guestConfirmationEnabled: !!_.get(settings, 'guestConfirmationEnabled', true),
        notificationRecipients: normalizeNotificationRecipients(_.get(settings, 'notificationRecipients')),
        onlineHint: _.get(settings, 'onlineHint', '') || null
    };

    if (_.get(settings, 'id')) {
        payload.id = toInteger(_.get(settings, 'id'), null);
    }

    return payload;
}

export function isTerminalReservationStatus(status) {
    return ['completed', 'cancelled', 'no_show'].includes(status);
}

export function getTableDisplayStatus(table, reservations, date, currentTime) {
    if (!table) {
        return 'available';
    }

    if (table.blocked) {
        return 'blocked';
    }

    const matches = (reservations || []).filter((reservation) => {
        return reservation.date === date
            && !isTerminalReservationStatus(reservation.status)
            && (reservation.tableIds || []).includes(String(table.id));
    });

    if (!matches.length) {
        return 'available';
    }

    const isActiveDay = !!date && isToday(parse(date, 'yyyy-MM-dd', new Date()));
    if (!isActiveDay || !currentTime) {
        return 'reserved';
    }

    const currentMinutes = toMinutes(currentTime);
    const hasOccupiedReservation = matches.some((reservation) => {
        if (reservation.status === 'seated') return true;
        const startMinutes = toMinutes(reservation.time);
        const endMinutes = startMinutes + toInteger(reservation.duration, 90);
        return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    });

    return hasOccupiedReservation ? 'occupied' : 'reserved';
}

export function toMinutes(time) {
    if (!time) {
        return 0;
    }

    const parts = String(time).split(':');
    return toInteger(parts[0], 0) * 60 + toInteger(parts[1], 0);
}

export function findNextReservationForTable(reservation, allReservations) {
    if (!reservation) {
        return null;
    }

    const startMinutes = toMinutes(reservation.time);
    const tableIds = reservation.tableIds || [];
    const sameTable = (allReservations || []).filter((r) => {
        return r.id !== reservation.id
            && r.date === reservation.date
            && !isTerminalReservationStatus(r.status)
            && toMinutes(r.time) > startMinutes
            && (r.tableIds || []).some((id) => { return tableIds.includes(id); });
    });

    sameTable.sort((a, b) => { return toMinutes(a.time) - toMinutes(b.time); });
    return sameTable[0] || null;
}

export function getEffectiveEndMinutes(reservation, allReservations, currentMinutes) {
    const scheduledEnd = toMinutes(reservation.time) + toInteger(reservation.duration, 90);
    if (reservation.status !== 'seated') {
        return scheduledEnd;
    }
    if (typeof currentMinutes !== 'number' || currentMinutes <= scheduledEnd) {
        return scheduledEnd;
    }

    const next = findNextReservationForTable(reservation, allReservations);
    const cap = next ? toMinutes(next.time) : Infinity;
    return Math.min(Math.max(scheduledEnd, currentMinutes), cap);
}

export function isTableAvailableInWindow(table, reservations, date, time, duration, excludeReservationId) {
    if (!table || table.blocked) {
        return false;
    }

    const startMinutes = toMinutes(time);
    const endMinutes = startMinutes + toInteger(duration, 90);
    const tableId = String(table.id);
    const excludeId = excludeReservationId ? String(excludeReservationId) : null;

    return !(reservations || []).some((reservation) => {
        if (excludeId && String(reservation.id) === excludeId) {
            return false;
        }
        if (reservation.date !== date) {
            return false;
        }
        if (isTerminalReservationStatus(reservation.status)) {
            return false;
        }
        if (!(reservation.tableIds || []).includes(tableId)) {
            return false;
        }

        const resStart = toMinutes(reservation.time);
        const resEnd = resStart + toInteger(reservation.duration, 90);
        return startMinutes < resEnd && endMinutes > resStart;
    });
}

export function pickAutoAssignment(tables, combinations, reservations, reservation) {
    const partySize = toInteger(_.get(reservation, 'partySize'), 0);
    if (partySize <= 0) {
        return null;
    }

    const date = _.get(reservation, 'date');
    const time = _.get(reservation, 'time');
    const duration = toInteger(_.get(reservation, 'duration'), 90);
    const excludeId = _.get(reservation, 'id');

    if (!date || !time) {
        return null;
    }

    const availableTables = (tables || []).filter((table) => {
        return isTableAvailableInWindow(table, reservations, date, time, duration, excludeId);
    });

    const fittingSingles = availableTables
        .filter((table) => { return toInteger(table.seats, 0) >= partySize; })
        .slice()
        .sort((a, b) => {
            const wasteA = toInteger(a.seats, 0) - partySize;
            const wasteB = toInteger(b.seats, 0) - partySize;
            if (wasteA !== wasteB) {
                return wasteA - wasteB;
            }
            return toInteger(a.seats, 0) - toInteger(b.seats, 0);
        });

    if (fittingSingles.length > 0) {
        return { tableIds: [String(fittingSingles[0].id)] };
    }

    const availableIds = {};
    availableTables.forEach((table) => { availableIds[String(table.id)] = true; });

    const seatsByTable = {};
    (tables || []).forEach((table) => { seatsByTable[String(table.id)] = toInteger(table.seats, 0); });

    const fittingCombos = (combinations || [])
        .map((combination) => {
            const tableIds = (combination.tableIds || []).map(String);
            const totalSeats = tableIds.reduce((sum, id) => {
                return sum + (seatsByTable[id] || 0);
            }, 0);
            return {
                combination: combination,
                tableIds: tableIds,
                totalSeats: totalSeats,
                allAvailable: tableIds.length >= 2 && tableIds.every((id) => { return availableIds[id]; })
            };
        })
        .filter((entry) => { return entry.allAvailable && entry.totalSeats >= partySize; })
        .sort((a, b) => {
            const wasteA = a.totalSeats - partySize;
            const wasteB = b.totalSeats - partySize;
            if (wasteA !== wasteB) {
                return wasteA - wasteB;
            }
            return a.tableIds.length - b.tableIds.length;
        });

    if (fittingCombos.length > 0) {
        return { tableIds: fittingCombos[0].tableIds.slice() };
    }

    return null;
}
